import { api, Packaging, PharmacyProduct, StockItem } from "@meditect/medibase-api-client";

import fetchLocalData from "@/features/offline/utils/fetchLocalData";

import { Slice } from "../boundStore";

const MAX_PAGES = 50;

export type PharmacyProductSlice = {
  error: boolean;
  fetchPharmacyProducts: () => Promise<void>;
  isFetching: boolean;
  pharmacyProducts: PharmacyProduct[];
  getPharmacyProductById: (id: string) => PharmacyProduct | undefined;

  removePharmacyProduct: (pharmacyProduct: PharmacyProduct) => void;
  replacePharmacyProduct: (pharmacyProduct: PharmacyProduct) => void;
  resetData: () => void;
  updatedAt: Date | undefined;
};

const fetchPagePharmacyProducts = async (cursor?: string) => {
  return api.pharmacyProduct.getAll({ query: { cursor } });
};

const fetchAllPharmacyProducts = async () => {
  const data: Awaited<ReturnType<typeof fetchPagePharmacyProducts>>["items"] = [];

  let cursor: string | undefined;
  let previousCursor: string | undefined;
  let iterations = 0;

  do {
    const res = await fetchPagePharmacyProducts(cursor);
    data.push(...res.items);

    previousCursor = cursor;
    cursor = res.nextCursor ?? undefined;

    iterations++;

    if (iterations >= MAX_PAGES) {
      console.warn("[fetchAllPharmacyProducts] Max pagination limit reached", { cursor });
      break;
    }

    if (cursor && cursor === previousCursor) {
      console.error("[fetchAllPharmacyProducts] Cursor did not advance", { cursor });
      break;
    }
  } while (cursor);

  return data as unknown as PharmacyProduct[]; // FIXME RPA: remove unused data from PharmacyProductSlice
};

export const createPharmacyProductSlice: Slice<PharmacyProductSlice> = (set, get) => ({
  error: false,
  // Fetcher
  fetchPharmacyProducts: async () => {
    await fetchLocalData(set, get, "pharmacyProduct.pharmacyProducts", fetchAllPharmacyProducts);
  },
  isFetching: false,
  // State
  pharmacyProducts: [],

  removePharmacyProduct: (pharmacyProduct: PharmacyProduct) =>
    set(({ pharmacyProduct: state }) => {
      const index = state.pharmacyProducts.findIndex((php) => php.id === pharmacyProduct.id);
      if (index !== -1) {
        state.pharmacyProducts.splice(index, 1);
      }
    }),

  // Getter
  getPharmacyProductById: (id: string) => {
    const state = get().pharmacyProduct;
    return state.pharmacyProducts.find((php) => php.id === id);
  },

  // Setter
  replacePharmacyProduct: (pharmacyProduct: PharmacyProduct) =>
    set(({ pharmacyProduct: state }) => {
      const index = state.pharmacyProducts.findIndex((php) => php.id === pharmacyProduct.id);

      if (index === -1) {
        state.pharmacyProducts.push(pharmacyProduct);
        return;
      }
      state.pharmacyProducts[index] = {
        ...pharmacyProduct,
        quantitiesByPackaging: pharmacyProduct.stockItems
          ?.filter((si): si is { packaging: Packaging } & StockItem => !!si.packaging)
          .reduce<Record<string, number>>((quantitiesByPackaging, si) => {
            quantitiesByPackaging[si.packaging.id] = (quantitiesByPackaging[si.packaging.id] ?? 0) + si.quantity;
            return quantitiesByPackaging;
          }, {}),
        quantity: pharmacyProduct.stockItems.reduce((_, si) => (si.packaging?.id ? 0 : si.quantity), 0),
      };
    }),

  resetData: () =>
    set(({ pharmacyProduct: state }) => {
      state.pharmacyProducts = [];
      state.error = false;
      state.updatedAt = undefined;
    }),

  updatedAt: undefined,
});
